import { useState, useEffect } from 'react';
import { supabase } from '../../../lib/supabase';
import { useAuth } from '../../../hooks/useAuth';

interface ProcedureStep {
  id: string;
  title: string;
  description: string;
  order: number;
}

interface ResearchProcedureFormProps {
  onSuccess?: () => void;
}

export default function ResearchProcedureForm({ onSuccess }: ResearchProcedureFormProps) {
  const { user } = useAuth();
  const [researchThemes, setResearchThemes] = useState<any[]>([]);
  const [selectedThemeId, setSelectedThemeId] = useState('');
  const [procedureTitle, setProcedureTitle] = useState('');
  const [steps, setSteps] = useState<ProcedureStep[]>([
    { id: crypto.randomUUID(), title: '', description: '', order: 1 }
  ]);
  const [loading, setLoading] = useState(false);
  const [message, setMessage] = useState('');

  useEffect(() => {
    fetchResearchThemes();
  }, [user]);

  const fetchResearchThemes = async () => {
    if (!user) return;

    const { data, error } = await supabase
      .from('research_overview')
      .select('id, title, theme')
      .eq('user_id', user.id)
      .order('created_at', { ascending: false });

    if (!error && data) {
      setResearchThemes(data);
      if (data.length > 0) {
        setSelectedThemeId(data[0].id);
      }
    }
  };

  const addStep = () => {
    const newStep: ProcedureStep = {
      id: crypto.randomUUID(),
      title: '',
      description: '',
      order: steps.length + 1
    };
    setSteps([...steps, newStep]);
  };

  const removeStep = (id: string) => {
    if (steps.length <= 1) {
      setMessage('最低1つの手順が必要です');
      setTimeout(() => setMessage(''), 3000);
      return;
    }
    const newSteps = steps.filter(step => step.id !== id);
    // 順序を再調整
    const reorderedSteps = newSteps.map((step, index) => ({
      ...step,
      order: index + 1
    }));
    setSteps(reorderedSteps);
  };

  const updateStep = (id: string, field: 'title' | 'description', value: string) => {
    setSteps(steps.map(step => 
      step.id === id ? { ...step, [field]: value } : step
    ));
  };

  const moveStep = (index: number, direction: 'up' | 'down') => {
    if (direction === 'up' && index === 0) return;
    if (direction === 'down' && index === steps.length - 1) return;

    const newSteps = [...steps];
    const targetIndex = direction === 'up' ? index - 1 : index + 1;
    [newSteps[index], newSteps[targetIndex]] = [newSteps[targetIndex], newSteps[index]];
    
    // 順序を再調整
    const reorderedSteps = newSteps.map((step, idx) => ({
      ...step,
      order: idx + 1
    }));
    setSteps(reorderedSteps);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!user || !selectedThemeId) {
      setMessage('研究テーマを選択してください');
      return;
    }

    if (!procedureTitle.trim()) {
      setMessage('手順タイトルを入力してください');
      return;
    }

    const hasEmptySteps = steps.some(step => !step.title.trim() || !step.description.trim());
    if (hasEmptySteps) {
      setMessage('すべての手順のタイトルと説明を入力してください');
      return;
    }

    setLoading(true);
    setMessage('');

    try {
      // 既存のprogress_metricsを取得
      const { data: existingData } = await supabase
        .from('research_overview')
        .select('progress_metrics')
        .eq('id', selectedThemeId)
        .single();

      const existingMetrics = existingData?.progress_metrics || {};

      // 研究手順データを追加
      const procedureData = {
        title: procedureTitle,
        steps: steps.map(({ id, ...rest }) => rest), // idを除外してシンプルに
        updated_at: new Date().toISOString()
      };

      const updatedMetrics = {
        ...existingMetrics,
        research_procedure: procedureData
      };

      const { error } = await supabase
        .from('research_overview')
        .update({ 
          progress_metrics: updatedMetrics,
          updated_at: new Date().toISOString()
        })
        .eq('id', selectedThemeId);

      if (error) throw error;

      setMessage('研究手順を保存しました');
      setProcedureTitle('');
      setSteps([{ id: crypto.randomUUID(), title: '', description: '', order: 1 }]);
      
      if (onSuccess) {
        setTimeout(() => {
          onSuccess();
        }, 1500);
      }
    } catch (error: any) {
      setMessage('保存に失敗しました: ' + error.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="bg-white rounded-lg shadow-sm p-6">
      <h2 className="text-2xl font-bold mb-6 text-gray-800">研究手順の記録</h2>
      
      <form onSubmit={handleSubmit} className="space-y-6">
        {/* 研究テーマ選択 */}
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            研究テーマ <span className="text-red-500">*</span>
          </label>
          <select
            value={selectedThemeId}
            onChange={(e) => setSelectedThemeId(e.target.value)}
            className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-transparent"
            required
          >
            <option value="">選択してください</option>
            {researchThemes.map((theme) => (
              <option key={theme.id} value={theme.id}>
                {theme.theme || theme.title}
              </option>
            ))}
          </select>
        </div>

        {/* 手順タイトル */}
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            手順タイトル <span className="text-red-500">*</span>
          </label>
          <input
            type="text"
            value={procedureTitle}
            onChange={(e) => setProcedureTitle(e.target.value)}
            placeholder="例: データ収集から分析までの手順"
            className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-transparent"
            required
          />
        </div>

        {/* 手順ステップ */}
        <div>
          <div className="flex items-center justify-between mb-3">
            <label className="block text-sm font-medium text-gray-700">
              研究手順 <span className="text-red-500">*</span>
            </label>
            <button
              type="button"
              onClick={addStep}
              className="flex items-center gap-2 px-3 py-1.5 bg-teal-500 text-white rounded-lg hover:bg-teal-600 transition-colors text-sm whitespace-nowrap"
            >
              <i className="ri-add-line"></i>
              手順を追加
            </button>
          </div>

          <div className="space-y-4">
            {steps.map((step, index) => (
              <div key={step.id} className="border border-gray-200 rounded-lg p-4 bg-gray-50">
                <div className="flex items-center justify-between mb-3">
                  <span className="text-sm font-semibold text-teal-600">
                    ステップ {step.order}
                  </span>
                  <div className="flex items-center gap-2">
                    {index > 0 && (
                      <button
                        type="button"
                        onClick={() => moveStep(index, 'up')}
                        className="w-8 h-8 flex items-center justify-center text-gray-600 hover:bg-gray-200 rounded transition-colors"
                        title="上に移動"
                      >
                        <i className="ri-arrow-up-line"></i>
                      </button>
                    )}
                    {index < steps.length - 1 && (
                      <button
                        type="button"
                        onClick={() => moveStep(index, 'down')}
                        className="w-8 h-8 flex items-center justify-center text-gray-600 hover:bg-gray-200 rounded transition-colors"
                        title="下に移動"
                      >
                        <i className="ri-arrow-down-line"></i>
                      </button>
                    )}
                    <button
                      type="button"
                      onClick={() => removeStep(step.id)}
                      className="w-8 h-8 flex items-center justify-center text-red-600 hover:bg-red-50 rounded transition-colors"
                      title="削除"
                    >
                      <i className="ri-delete-bin-line"></i>
                    </button>
                  </div>
                </div>

                <div className="space-y-3">
                  <input
                    type="text"
                    value={step.title}
                    onChange={(e) => updateStep(step.id, 'title', e.target.value)}
                    placeholder="手順のタイトル（例: データ収集）"
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-transparent text-sm"
                    required
                  />
                  <textarea
                    value={step.description}
                    onChange={(e) => updateStep(step.id, 'description', e.target.value)}
                    placeholder="手順の詳細説明を入力してください"
                    rows={3}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-transparent text-sm resize-none"
                    required
                  />
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* メッセージ */}
        {message && (
          <div className={`p-4 rounded-lg ${
            message.includes('失敗') || message.includes('必要') 
              ? 'bg-red-50 text-red-700' 
              : 'bg-green-50 text-green-700'
          }`}>
            {message}
          </div>
        )}

        {/* 送信ボタン */}
        <button
          type="submit"
          disabled={loading}
          className="w-full py-3 bg-teal-500 text-white rounded-lg hover:bg-teal-600 transition-colors font-medium disabled:opacity-50 disabled:cursor-not-allowed whitespace-nowrap"
        >
          {loading ? '保存中...' : '研究手順を保存'}
        </button>
      </form>
    </div>
  );
}